import React, { useEffect } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/client';
import { CircularProgress, ThemeProvider } from '@material-ui/core';

import { Product, getTheme } from '@/utils/productHelpers';
import { getSupabaseClient } from '@/utils/supabaseClient';
import { activateSparkConfirm, requestAuthCode } from '@/utils/sparkActivationApi';
import {
  REDIRECT_TO_SESSION_KEY, ACCESS_CODE_SESSION_KEY, EMPLOYER_ORG_SLUG_KEY, PRODUCT_DEEP_LINKS,
} from '@/utils/constants';
import { isSafeRedirectUri } from '@/utils/urlHelpers';
import { ME_QUERY } from '@/apollo/queries';
import { ACTIVATE_USER_BY_ACCESS_CODE } from '@/apollo/mutations';

/**
 * Extract entitlement status from a Supabase JWT.
 * Returns 'active', 'pending', or undefined if not present.
 */
const getEntitlementStatus = (accessToken: string): string | undefined => {
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    return payload.bh_claims?.products?.[0]?.status;
  } catch {
    return undefined;
  }
};

const EmailAuthCallback: React.FC = () => {
  const history = useHistory();
  const { product } = useParams<{ product: Product }>();
  const { search } = useLocation();
  const [fetchMe] = useLazyQuery<{ me: { uid: string } }>(ME_QUERY, { fetchPolicy: 'no-cache' });
  const [activateUserByAccessCode] = useMutation(ACTIVATE_USER_BY_ACCESS_CODE);

  useEffect(() => {
    // Login flow: user already active — skip activation, go straight to session handoff.
    // Same pattern as SmsVerify's fromLogin path.
    const handleLoginFlow = async () => {
      const {
        data: { session: refreshedSession },
        error: refreshError,
      } = await getSupabaseClient().auth.refreshSession();

      if (refreshError || !refreshedSession?.access_token || !refreshedSession?.refresh_token) {
        console.error('[EmailAuthCallback] refreshSession failed:', refreshError?.message);
        history.replace('/spark/login');
        return;
      }

      const redirectTo = sessionStorage.getItem(REDIRECT_TO_SESSION_KEY)
        || PRODUCT_DEEP_LINKS[product];
      if (!redirectTo || !isSafeRedirectUri(redirectTo)) {
        console.error('[EmailAuthCallback] redirectTo is missing or invalid');
        history.replace('/spark/login');
        return;
      }

      let exchangeToken: string;
      try {
        ({ exchangeToken } = await requestAuthCode(
          refreshedSession.access_token,
          refreshedSession.refresh_token,
        ));
      } catch (err) {
        console.error('[EmailAuthCallback] requestAuthCode failed:', err);
        history.replace('/spark/login');
        return;
      }

      const { data: meData } = await fetchMe({
        context: {
          headers: { Authorization: `Bearer ${refreshedSession.access_token}` },
        },
      });
      const limbixUserUid = meData?.me?.uid;

      const deepLinkUrl = new URL(redirectTo);
      deepLinkUrl.searchParams.set('exchange_token', exchangeToken);
      if (limbixUserUid) {
        deepLinkUrl.searchParams.set('limbix_user_uid', limbixUserUid);
      }

      window.location.href = deepLinkUrl.toString();
    };

    const handleCallback = async () => {
      try {
        // Wait for Supabase to process the hash fragment (detectSessionInUrl is async)
        await new Promise((resolve) => { setTimeout(resolve, 1500); });

        const { data: { session } } = await getSupabaseClient().auth.getSession();

        if (!session) {
          history.replace(`/activate/${product}/access-code`, {
            error: 'email_verification_failed',
          });
          return;
        }

        const supabaseUserId = session.user?.id ?? '';
        const patientUid = session.user?.user_metadata?.limbixUserId || '';

        // Determine flow from the JWT itself — no sessionStorage dependency.
        // Returning users have status: 'active', new users have status: 'pending'.
        const entitlementStatus = getEntitlementStatus(session.access_token);
        const isReturningUser = entitlementStatus === 'active';

        if (isReturningUser) {
          await handleLoginFlow();
          return;
        }

        // Activation flow: new user — confirm entitlement and activate in Cortex
        const accessCode = sessionStorage.getItem(ACCESS_CODE_SESSION_KEY);

        try {
          await Promise.all([
            activateSparkConfirm({ supabaseUserId, accessToken: session.access_token }),
            ...(accessCode
              ? [activateUserByAccessCode({
                variables: { accessCode },
                context: { headers: { Authorization: `Bearer ${session.access_token}` } },
              })]
              : []),
          ]);
        } catch (err) {
          console.error('[EmailAuthCallback] activation failed:', err);
        } finally {
          sessionStorage.removeItem(ACCESS_CODE_SESSION_KEY);
        }

        // Store redirect target for UserInfoConfirmation session handoff
        const redirectTo = PRODUCT_DEEP_LINKS[product];
        if (redirectTo && isSafeRedirectUri(redirectTo)) {
          sessionStorage.setItem(REDIRECT_TO_SESSION_KEY, redirectTo);
        }

        const orgSlug = new URLSearchParams(search).get('org_slug');
        if (orgSlug) {
          sessionStorage.setItem(EMPLOYER_ORG_SLUG_KEY, orgSlug);
        }

        history.replace(`/activate/${product}/user-info/confirmation`, {
          patientUid,
          flow: 'email',
        });
      } catch (err) {
        console.error('[EmailAuthCallback] Unexpected error:', err);
        history.replace(`/activate/${product}/access-code`, {
          error: 'email_verification_failed',
        });
      }
    };

    handleCallback();
  }, [history, product, fetchMe, activateUserByAccessCode]);

  return (
    <ThemeProvider theme={getTheme(product)}>
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh',
      }}
      >
        <CircularProgress color="primary" />
      </div>
    </ThemeProvider>
  );
};

export default EmailAuthCallback;
